<template>
  <div id="dialog-counsel-history">
    <span class="cls" @click="oth_close()"></span>
    <div class="paper">

      <!-- 헤더 -->
      <div class="header">
        <h1><i class="ph ph-clock-counter-clockwise"></i> 상담 이력</h1>
        <span class="nav-btn close" @click="oth_close()"><i class="ph ph-x"></i></span>
      </div>

      <!-- 로딩 -->
      <div class="content loading" v-if="loading">
        <i class="ph ph-spinner"></i>
        <p>불러오는 중...</p>
      </div>

      <!-- 빈 상태 -->
      <div class="content empty" v-else-if="historyList.length === 0">
        <i class="ph ph-folder-open"></i>
        <p>상담 이력이 없습니다</p>
      </div>

      <!-- 리스트 -->
      <div class="content list" v-else-if="!selectedHistory">
        <ul>
          <li v-for="item in historyList" :key="item.idx" @click="viewDetail(item)">
            <div class="item-left">
              <div class="customer-avatar">
                <img :src="item.customer_image || '/imgs/unknow_user.png'" />
              </div>
              <div class="item-info">
                <h3>{{ item.customer_name }}</h3>
                <p class="counsel-title">{{ item.counsel_title }}</p>
                <span class="date">{{ formatDate(item.completed_at || item.started_at) }}</span>
              </div>
            </div>
            <div class="item-right">
              <span class="status" :class="item.status">
                {{ item.status === 'completed' ? '완료' : '진행중' }}
              </span>
              <i class="ph ph-caret-right"></i>
            </div>
          </li>
        </ul>
      </div>

      <!-- 상세 보기 -->
      <div class="content detail" v-else>
        <div class="detail-header">
          <button class="back-btn" @click="selectedHistory = null">
            <i class="ph ph-caret-left"></i> 목록
          </button>
          <div class="detail-meta">
            <h2>{{ selectedHistory.customer_name }}</h2>
            <p>{{ selectedHistory.counsel_title }} · {{ formatDate(selectedHistory.completed_at) }}</p>
          </div>
        </div>
        <div class="detail-list">
          <div class="detail-item" v-for="(qa, index) in parsedAnswers" :key="index">
            <span class="q-label">Q{{ index + 1 }}</span>
            <div class="qa-content">
              <p class="question">{{ qa.question }}</p>
              <p class="answer">{{ qa.answer }}</p>
            </div>
          </div>
          <div class="no-answers" v-if="parsedAnswers.length === 0">
            <p>저장된 답변이 없습니다</p>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import { ref, inject, onMounted, computed } from 'vue';

export default {
  props: {
    oth_close: {
      type: Function,
      required: true,
    },
  },
  setup() {
    const sql_query = inject('sql_query');
    const member_info = inject('member_info');
    
    const loading = ref(true);
    const historyList = ref([]);
    const selectedHistory = ref(null);
    const pageOptionsCache = ref({});

    async function fetchHistory() {
      loading.value = true;
      
      if (!member_info?.mb_id) {
        loading.value = false;
        return;
      }
      
      const sql = `
        SELECT 
          h.*,
          c.name as customer_name,
          c.image as customer_image,
          s.title as counsel_title
        FROM insu_counsel_history h
        LEFT JOIN customers c ON h.customer_idx = c.idx
        LEFT JOIN insu_counsel_set s ON h.counsel_set_idx = s.idx
        WHERE h.mb_id = '${member_info.mb_id}'
        ORDER BY h.started_at DESC
        LIMIT 50
      `;

      console.log(sql);
      
      const res = await sql_query(sql);
      historyList.value = res || [];
      loading.value = false;
    }

    async function viewDetail(item) {
      selectedHistory.value = item;
      
      // 답변 파싱을 위해 페이지 옵션 정보 로드
      if (item.answers) {
        const answers = JSON.parse(item.answers);
        const pageIdxs = Object.keys(answers);
        
        for (const pageIdx of pageIdxs) {
          if (!pageOptionsCache.value[pageIdx]) {
            const pageRes = await sql_query(`SELECT question FROM insu_counsel_page WHERE idx = ${pageIdx}`);
            const optRes = await sql_query(`SELECT * FROM insu_counsel_page_option WHERE page_idx = ${pageIdx} ORDER BY option_no ASC`);
            pageOptionsCache.value[pageIdx] = {
              question: pageRes?.[0]?.question || '',
              options: optRes || []
            };
          }
        }
      }
    }

    const parsedAnswers = computed(() => {
      if (!selectedHistory.value?.answers) return [];
      
      const answers = JSON.parse(selectedHistory.value.answers);
      const result = [];
      
      for (const [pageIdx, optionIdx] of Object.entries(answers)) {
        const cache = pageOptionsCache.value[pageIdx];
        if (cache) {
          const option = cache.options.find(o => o.option_no === optionIdx + 1);
          result.push({
            question: cache.question,
            answer: option?.option_text || `선택 ${optionIdx + 1}`
          });
        }
      }
      
      return result;
    });

    function formatDate(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      const h = String(date.getHours()).padStart(2, '0');
      const min = String(date.getMinutes()).padStart(2, '0');
      return `${y}.${m}.${d} ${h}:${min}`;
    }

    onMounted(() => {
      fetchHistory();
    });

    return {
      loading,
      historyList,
      selectedHistory,
      viewDetail,
      parsedAnswers,
      formatDate
    };
  }
}
</script>

<style scoped>
#dialog-counsel-history {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(8px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 100000;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

#dialog-counsel-history > .cls {
  position: absolute;
  inset: 0;
  z-index: -1;
}

.paper {
  width: 100%;
  max-width: 600px;
  max-height: 80vh;
  background: var(--color-background-light);
  border-radius: 24px;
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.4);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: slideUp 0.4s ease;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* 헤더 */
.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid var(--color-background-light);
  flex-shrink: 0;
}

.header h1 {
  font-size: var(--font-size-lg);
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
}

.header h1 i {
  color: var(--color-brand);
}

.nav-btn {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--font-size-xl);
  cursor: pointer;
  border-radius: 12px;
  transition: all 0.2s ease;
}

.nav-btn:hover {
  background: var(--color-background-light);
}

.nav-btn.close:hover {
  background: rgba(218, 47, 71, 0.1);
  color: var(--color-special-warning);
}

/* 컨텐츠 공통 */
.content {
  flex: 1;
  overflow: hidden;
}

.content.loading,
.content.empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 60px 24px;
  color: var(--color-text-secondary);
}

.content.loading i,
.content.empty i {
  font-size: 48px;
}

.content.loading i {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* 리스트 */
.content.list {
  overflow-y: auto;
}

.content.list ul {
  list-style: none;
  margin: 0;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.content.list li {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  background: var(--color-surface-light);
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid transparent;
}

.content.list li:hover {
  background: var(--color-background-light);
  border-color: var(--color-brand);
}

.item-left {
  display: flex;
  align-items: center;
  gap: 14px;
  flex: 1;
  min-width: 0;
}

.customer-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
}

.customer-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.item-info {
  flex: 1;
  min-width: 0;
}

.item-info h3 {
  font-size: var(--font-size-base);
  font-weight: 600;
  margin: 0 0 4px 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.item-info .counsel-title {
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  margin: 0 0 4px 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.item-info .date {
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
}

.item-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.status {
  font-size: var(--font-size-xs);
  padding: 4px 10px;
  border-radius: 100px;
  font-weight: 600;
}

.status.completed {
  background: rgba(34, 197, 94, 0.15);
  color: #22c55e;
}

.status.ongoing {
  background: rgba(255, 165, 0, 0.15);
  color: var(--color-brand);
}

.item-right i {
  font-size: var(--font-size-lg);
  color: var(--color-text-secondary);
}

/* 상세 보기 */
.content.detail {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.detail-header {
  padding: 16px 24px;
  border-bottom: 1px solid var(--color-background-light);
  display: flex;
  align-items: center;
  gap: 16px;
}

.back-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 12px;
  background: var(--color-background-light);
  border: none;
  border-radius: 8px;
  font-size: var(--font-size-sm);
  color: var(--color-text-primary);
  cursor: pointer;
  transition: all 0.2s;
}

.back-btn:hover {
  background: var(--color-surface-light);
}

.detail-meta h2 {
  font-size: var(--font-size-md);
  font-weight: 600;
  margin: 0 0 4px 0;
}

.detail-meta p {
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  margin: 0;
}

.detail-list {
  flex: 1;
  overflow-y: auto;
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.detail-item {
  display: flex;
  gap: 12px;
  padding: 16px;
  background: var(--color-surface-light);
  border-radius: 12px;
}

.q-label {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-brand);
  color: #000;
  border-radius: 8px;
  font-weight: 700;
  font-size: var(--font-size-xs);
  flex-shrink: 0;
}

.qa-content {
  flex: 1;
}

.qa-content .question {
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  margin: 0 0 6px 0;
}

.qa-content .answer {
  font-size: var(--font-size-base);
  font-weight: 600;
  margin: 0;
  color: var(--color-text-primary);
}

.no-answers {
  text-align: center;
  padding: 40px;
  color: var(--color-text-secondary);
}

/* 태블릿 (601px ~ 1024px) */
@media (min-width: 601px) and (max-width: 1024px) {
  .paper {
    width: 90%;
    max-width: 650px;
    max-height: 85vh;
    border-radius: 20px;
  }

  .header {
    padding: 18px 20px;
  }

  .header h1 {
    font-size: var(--font-size-base);
  }

  /* 리스트 2컬럼 그리드 */
  .content.list ul {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
    padding: 16px;
  }

  .content.list li {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    padding: 14px;
  }

  .item-left {
    width: 100%;
  }

  .item-right {
    width: 100%;
    justify-content: space-between;
  }

  .customer-avatar {
    width: 44px;
    height: 44px;
  }

  /* 상세 보기 */
  .detail-header {
    padding: 14px 20px;
  }

  .detail-list {
    padding: 16px 20px;
    gap: 12px;
  }

  .detail-item {
    padding: 14px;
  }
}

/* 모바일 (600px 이하) */
@media (max-width: 600px) {
  .paper {
    width: 100%;
    max-width: 100%;
    max-height: 100%;
    height: 100%;
    border-radius: 0;
  }

  .header {
    padding: 16px;
  }

  .header h1 {
    font-size: var(--font-size-base);
    gap: 8px;
  }

  .header h1 i {
    font-size: var(--font-size-lg);
  }

  .nav-btn {
    width: 36px;
    height: 36px;
  }

  /* 로딩/빈 상태 */
  .content.loading,
  .content.empty {
    padding: 48px 20px;
  }

  .content.loading i,
  .content.empty i {
    font-size: 40px;
  }

  /* 리스트 */
  .content.list ul {
    padding: 12px;
    gap: 10px;
  }

  .content.list li {
    padding: 14px;
    border-radius: 14px;
  }

  .item-left {
    gap: 12px;
  }

  .customer-avatar {
    width: 42px;
    height: 42px;
  }

  .item-info h3 {
    font-size: var(--font-size-sm);
  }

  .item-info .counsel-title {
    font-size: var(--font-size-xs);
  }

  .item-right {
    gap: 8px;
  }

  .status {
    padding: 3px 8px;
    font-size: 10px;
  }

  /* 상세 보기 */
  .detail-header {
    padding: 12px 16px;
    gap: 12px;
  }

  .back-btn {
    padding: 6px 10px;
    font-size: var(--font-size-xs);
  }

  .detail-meta h2 {
    font-size: var(--font-size-base);
  }

  .detail-meta p {
    font-size: var(--font-size-xs);
  }

  .detail-list {
    padding: 16px;
    gap: 12px;
  }

  .detail-item {
    padding: 12px;
    gap: 10px;
    border-radius: 10px;
  }

  .q-label {
    width: 28px;
    height: 28px;
    font-size: 10px;
    border-radius: 6px;
  }

  .qa-content .question {
    font-size: var(--font-size-xs);
  }

  .qa-content .answer {
    font-size: var(--font-size-sm);
  }

  .no-answers {
    padding: 32px;
  }
}
</style>